import { useState, type ReactNode } from 'react';
import type { DocBlockStyle, DocInlineMark } from '@itsappy/shared';
import { cn } from '@/lib/utils';
import {
  BoldIcon,
  BulletListIcon,
  ChecklistIcon,
  CodeIcon,
  DividerIcon,
  ItalicIcon,
  LinkIcon,
  NumberListIcon,
  QuoteIcon,
  StrikethroughIcon,
  TextToolIcon,
} from '@/components/ui/icons';

/**
 * The formatting strip above a document.
 *
 * Presentational on purpose: the editor owns the paragraphs and the socket, so
 * the toolbar is handed what the caret is currently in and the four things it
 * can ask for. That keeps every edit going through the same one path the
 * keyboard already uses, rather than a second one that would have to learn
 * about collaboration on its own.
 *
 * The buttons deliberately mirror the markdown shortcuts that already work —
 * "## " and the bold button do the same thing to the same paragraph — because
 * the shortcuts are invisible and a toolbar is how people find out the
 * document can do any of this.
 */

export interface DocToolbarProps {
  /** The style of the paragraph the caret is in, for the pressed state. */
  style: DocBlockStyle | null;
  /** False while the caret is nowhere, or the document is read only. */
  disabled: boolean;
  onStyle: (style: DocBlockStyle) => void;
  onMark: (mark: DocInlineMark) => void;
  onDivider: () => void;
  onLink: (href: string) => void;
  className?: string;
}

/** Body text and the three heading levels, as one row of paragraph kinds. */
const BLOCKS: { style: DocBlockStyle; label: string; content: ReactNode }[] = [
  { style: 'p', label: 'Body text', content: <TextToolIcon size="sm" /> },
  {
    style: 'h1',
    label: 'Heading 1',
    content: <span className="text-xs font-semibold">H1</span>,
  },
  {
    style: 'h2',
    label: 'Heading 2',
    content: <span className="text-xs font-semibold">H2</span>,
  },
  {
    style: 'h3',
    label: 'Heading 3',
    content: <span className="text-xs font-semibold">H3</span>,
  },
  { style: 'bullet', label: 'Bulleted list', content: <BulletListIcon size="sm" /> },
  { style: 'number', label: 'Numbered list', content: <NumberListIcon size="sm" /> },
  { style: 'todo', label: 'To-do', content: <ChecklistIcon size="sm" /> },
  { style: 'quote', label: 'Quote', content: <QuoteIcon size="sm" /> },
  { style: 'code', label: 'Code block', content: <CodeIcon size="sm" /> },
];

const MARKS: { mark: DocInlineMark; label: string; content: ReactNode }[] = [
  { mark: 'bold', label: 'Bold', content: <BoldIcon size="sm" /> },
  { mark: 'italic', label: 'Italic', content: <ItalicIcon size="sm" /> },
  { mark: 'strike', label: 'Strikethrough', content: <StrikethroughIcon size="sm" /> },
  /* A glyph rather than the code icon: that one already means "code block"
     one group to the left, and the same drawing for two different things is
     how people learn that neither does what they expected. */
  {
    mark: 'code',
    label: 'Inline code',
    content: <span className="font-mono text-xs leading-none">{'`'}</span>,
  },
];

export function ToolbarButton({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      /* Held rather than clicked: a click moves focus out of the paragraph,
         and the selection the button is about to format is gone by the time
         the handler runs. Acting on mousedown keeps it. */
      onMouseDown={(event) => {
        event.preventDefault();
        onClick();
      }}
      className={cn(
        'grid h-7 min-w-7 place-items-center rounded-md px-1.5 text-slate-400 transition-colors',
        'hover:bg-surface-3 hover:text-ink disabled:pointer-events-none disabled:opacity-40',
        pressed && 'bg-surface-4 text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** A hairline between groups of buttons, so the strip reads as groups. */
const Divider = () => <span aria-hidden className="mx-0.5 h-5 w-px bg-surface-3" />;

export function DocToolbar({
  style,
  disabled,
  onStyle,
  onMark,
  onDivider,
  onLink,
  className,
}: DocToolbarProps) {
  /* The address is typed in the strip rather than in a browser prompt: a
     `window.prompt` is modal to the whole tab, looks nothing like the rest of
     the app, and on some browsers is suppressed outright. */
  const [href, setHref] = useState<string | null>(null);

  const confirmLink = () => {
    const address = href?.trim();
    setHref(null);
    if (address) onLink(address);
  };

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className={cn(
        'flex flex-wrap items-center gap-0.5 border-b border-surface-3 bg-surface-2 px-2 py-1.5',
        className,
      )}
    >
      {BLOCKS.map((block) => (
        <ToolbarButton
          key={block.style}
          label={block.label}
          pressed={style === block.style}
          disabled={disabled}
          onClick={() => onStyle(block.style)}
        >
          {block.content}
        </ToolbarButton>
      ))}

      <Divider />

      {MARKS.map((entry) => (
        <ToolbarButton
          key={entry.mark}
          label={entry.label}
          disabled={disabled}
          onClick={() => onMark(entry.mark)}
        >
          {entry.content}
        </ToolbarButton>
      ))}
      <ToolbarButton
        label="Link"
        pressed={href !== null}
        disabled={disabled}
        onClick={() => setHref((current) => (current === null ? '' : null))}
      >
        <LinkIcon size="sm" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton label="Divider" disabled={disabled} onClick={onDivider}>
        <DividerIcon size="sm" />
      </ToolbarButton>

      {href !== null && (
        <form
          className="flex w-full items-center gap-1.5 pt-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            confirmLink();
          }}
        >
          <input
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setHref(null);
            }}
            placeholder="https://…"
            autoFocus
            inputMode="url"
            aria-label="Address to link to"
            className="input h-7 flex-1 text-xs"
          />
          <button type="submit" className="btn-secondary h-7 px-2 text-xs">
            Link
          </button>
        </form>
      )}
    </div>
  );
}
